if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      disconnectedCallback() {
        this.navigationController?.abort();
      }

      setupNavigation(component) {
        const main = component.initSwiper;
        if (!main || main.destroyed) return;
        this.navigationController?.abort();
        this.navigationController = new AbortController();
        const { signal } = this.navigationController;
        const thumbs = component._thumbsSwiper;
        const counter = component.querySelector('.vh-gallery-counter');
        const previous = main.el.querySelector('.swiper-button-prev');
        const next = main.el.querySelector('.swiper-button-next');
        const thumbPrevious = component.querySelector('.vh-gallery-thumb-prev');
        const thumbNext = component.querySelector('.vh-gallery-thumb-next');
        const buttons = () => Array.from(component.querySelectorAll('.swiper-controls__thumbnail'));
        const setDisabled = (button, disabled) => {
          if (!button) return;
          button.disabled = disabled;
          button.setAttribute('aria-disabled', String(disabled));
        };
        const updateThumbNavigation = () => {
          if (!thumbs || thumbs.destroyed) return;
          setDisabled(thumbPrevious, thumbs.isLocked || thumbs.isBeginning);
          setDisabled(thumbNext, thumbs.isLocked || thumbs.isEnd);
        };
        const ensureActiveThumbnail = () => {
          if (!thumbs || thumbs.destroyed || main.destroyed) return;
          const index = main.activeIndex;
          const start = thumbs.slidesGrid[index];
          const size = thumbs.slidesSizesGrid[index];
          const offset = Math.abs(thumbs.translate);
          if (start < offset - 1 || start + size > offset + thumbs.size + 1) {
            thumbs.slideTo(index, 250);
          }
        };
        const update = () => {
          if (main.destroyed) return;
          const total = main.slides.length;
          const index = main.activeIndex;
          if (counter) counter.textContent = `${total ? index + 1 : 0} / ${total}`;
          setDisabled(previous, total <= 1 || main.isBeginning);
          setDisabled(next, total <= 1 || main.isEnd);
          buttons().forEach((button, i) => {
            button.classList.toggle('active', i === index);
            button.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
          updateThumbNavigation();
        };
        const sync = () => {
          update();
          requestAnimationFrame(ensureActiveThumbnail);
        };
        main.on('slideChange slidesLengthChange update resize transitionEnd', sync);
        thumbs?.on('progress resize lock unlock transitionEnd', updateThumbNavigation);
        signal.addEventListener('abort', () => {
          if (!main.destroyed) main.off('slideChange slidesLengthChange update resize transitionEnd', sync);
          if (thumbs && !thumbs.destroyed) thumbs.off('progress resize lock unlock transitionEnd', updateThumbNavigation);
        }, { once: true });
        component.addEventListener('click', (event) => {
          const button = event.target.closest('.swiper-controls__thumbnail');
          if (!button) return;
          const index = buttons().indexOf(button);
          if (index >= 0) main.slideTo(index);
        }, { signal });
        thumbPrevious?.addEventListener('click', () => thumbs?.slidePrev(), { signal });
        thumbNext?.addEventListener('click', () => thumbs?.slideNext(), { signal });
        component.addEventListener('keydown', (event) => {
          const thumbnail = event.target.closest('.swiper-controls__thumbnail');
          if (!thumbnail && event.target !== main.el) return;
          const rtl = main.rtl;
          let target = thumbnail ? buttons().indexOf(thumbnail) : main.activeIndex;
          if (event.key === 'ArrowRight') target += rtl ? -1 : 1;
          else if (event.key === 'ArrowLeft') target += rtl ? 1 : -1;
          else if (event.key === 'Home') target = 0;
          else if (event.key === 'End') target = main.slides.length - 1;
          else return;
          event.preventDefault();
          target = Math.max(0, Math.min(main.slides.length - 1, target));
          main.slideTo(target);
          if (thumbnail) buttons()[target]?.focus({ preventScroll: true });
        }, { signal });
        component.dataset.galleryNavigationReady = 'true';
        sync();
      }

      setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        let cleanMediaId = mediaId;
        if (mediaId.indexOf('-') !== -1) {
          const parts = mediaId.split('-');
          cleanMediaId = parts[parts.length - 1];
        }

        let element = this.querySelector(`[data-media-id="${cleanMediaId}"]`);
        
        if (!element) {
          element = this.querySelector(`[data-media-id="${mediaId}"]`);
        }

        if (!element) return false;

        this.querySelectorAll('[data-media-id].active').forEach((node) => node.classList.remove('active'));

        element.classList.add('active');

        const allMediaElements = Array.from(this.querySelectorAll('[data-media-id]'));
        const mediaIndex = allMediaElements.indexOf(element);

        this.syncSlideshow(element, mediaIndex, smooth);
        this.syncThumbnails(element, mediaIndex);

        if (this.classList.contains('media-gallery--grid')) {
          this.swapGridMedia(element, mediaIndex);
        }

        return true;
      }

      /**
       * Sync slideshow/swiper component when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       * @param {boolean} smooth - Whether to use smooth transition
       */
      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this.classList.contains('media-gallery--carousel')) return;

          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent) return;
          
          const waitForSwiper = (retries = 10) => {
            if (swiperComponent.initSwiper) {
              const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
              let slideIndex = mediaIndex;
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              }

              if (slideIndex >= 0 && slideIndex < slides.length && swiperComponent.initSwiper) {
                swiperComponent.initSwiper.slideTo(slideIndex, smooth ? 800 : 0);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForSwiper(retries - 1), 50);
            }
          };

          waitForSwiper();
        } catch (e) {
          try {
            if (this.slideshow && typeof this.slideshow.goTo === 'function') {
              this.slideshow.goTo(mediaIndex);
            }
          } catch (err) {
            // silent
          }
        }
      }

      /**
       * Sync thumbnails controls when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      syncThumbnails(mediaElement, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector('.swiper-controls__thumbnails-container');
          if (!thumbnailsContainer) return;

          const thumbnailButtons = Array.from(thumbnailsContainer.querySelectorAll('.swiper-controls__thumbnail[data-index]'));
          if (thumbnailButtons.length === 0) return;

          let slideIndex = mediaIndex;
          
          if (this.classList.contains('media-gallery--carousel')) {
            const swiperComponent = this.querySelector('swiper-component');
            if (swiperComponent) {
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              } else {
                if (swiperComponent.initSwiper && typeof swiperComponent.initSwiper.activeIndex === 'number') {
                  slideIndex = mediaIndex;
                }
              }
            }
          }

          let targetThumbnail = thumbnailButtons.find((button) => {
            const index = parseInt(button.getAttribute('data-index'), 10);
            return !isNaN(index) && index === slideIndex;
          });

          if (!targetThumbnail) {
            targetThumbnail = thumbnailButtons.find((button) => {
              const index = parseInt(button.getAttribute('data-index'), 10);
              return !isNaN(index) && index === mediaIndex;
            });
          }

          if (targetThumbnail) {
            const finalSlideIndex = parseInt(targetThumbnail.getAttribute('data-index'), 10);

            this.syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, finalSlideIndex);
          }
        } catch (e) {
          // silent
        }
      }

      /**
       * Sync thumbnail swiper to show the active thumbnail
       * @param {HTMLElement} thumbnailsContainer - The thumbnail container element
       * @param {Array} thumbnailButtons - Array of all thumbnail buttons
       * @param {HTMLElement} targetThumbnail - The active thumbnail button
       * @param {number} slideIndex - The slide index to sync to
       */
      syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, slideIndex) {
        try {
          const waitForThumbnailSwiper = (retries = 10) => {
            const swiperComponent = this.querySelector('swiper-component');
            let thumbsSwiper = null;

            if (swiperComponent && swiperComponent.initSwiper && swiperComponent.initSwiper.params && swiperComponent.initSwiper.params.thumbs) {
              thumbsSwiper = swiperComponent.initSwiper.params.thumbs.swiper;
            }

            if (!thumbsSwiper) {
              const thumbnailSwiper = thumbnailsContainer.querySelector('.swiper');
              if (thumbnailSwiper && thumbnailSwiper.swiper) {
                thumbsSwiper = thumbnailSwiper.swiper;
              }
            }

            if (thumbsSwiper) {
              if (slideIndex >= 0 && slideIndex < thumbnailButtons.length) {
                thumbsSwiper.slideTo(slideIndex, 300);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForThumbnailSwiper(retries - 1), 50);
            }
          };

          waitForThumbnailSwiper();
        } catch (e) {
          // silent
        }
      }

      /**
       * Swap media in grid layout: move variant image to first position
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      swapGridMedia() {
        try {
          if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
            const handleVariantUpdateOnce = (event) => {
              try {
                const source = event?.detail?.data?.html;
                if (!source) return;

                const newMediaGallery = source.querySelector('media-gallery');
                if (!newMediaGallery) return;
                this.replaceWith(newMediaGallery);

                try {
                  newMediaGallery.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // ignore scrolling errors
                }
              } catch (e) {
                // silent
              } finally {
                document.removeEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
              }
            };
            document.addEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
          }
          
        } catch (e) {
          // silent
        }
      }
    }
  );
}